'use client';

import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useAppColors } from '@/styles/colors';
import { SIDEBAR_TAB_PAPER_SX, SIDEBAR_TAB_HEADER_SX, sidebarTabButtonSx } from '@/components/ui/sidebar-tabs-style';
import type { BreakdownItem } from '@/libs/fii-cash-calculations';

export type CashTabViewModel = {
  referenceDate: string | null;
  sourceLabel: string;
  cnpjClasse: string | null;
  current: {
    cashTotal: number | null;
    cashBreakdown: BreakdownItem[];
    securitiesAndFundsTotal: number | null;
    securitiesAndFundsBreakdown: BreakdownItem[];
    receivableTotal: number | null;
    receivablesBreakdown: BreakdownItem[];
    payableTotal: number | null;
    payablesBreakdown: BreakdownItem[];
    patrimony: number | null;
    coverage: number | null;
    netCashCurrent: number | null;
    monthlyDeltaPayable: number | null;
    monthlyDeltaReceivable: number | null;
    monthlyDeltaSecuritiesAndFunds: number | null;
  };
  history: Array<{
    date: string;
    cashTotal: number | null;
    cashBreakdown: BreakdownItem[];
    securitiesAndFundsTotal: number | null;
    securitiesAndFundsBreakdown: BreakdownItem[];
    receivableTotal: number | null;
    receivablesBreakdown: BreakdownItem[];
    payableTotal: number | null;
    payablesBreakdown: BreakdownItem[];
    netCashPosition: number | null;
  }>;
};

type Props = {
  tickerLabel: string;
  data: CashTabViewModel;
  activeSubview?: PublicSubview;
  buildHref?: (subview: PublicSubview) => string;
};

type SubTab = 'overview' | 'cash' | 'securitiesFunds' | 'receivables' | 'payables';

type DonutDatum = { label: string; value: number };

const SECTION_LABEL: Record<SubTab, string> = {
  overview: 'Visão Geral',
  cash: 'Detalhe de Caixa',
  securitiesFunds: 'Detalhe de Títulos/Fundos',
  receivables: 'Detalhe de Recebíveis',
  payables: 'Detalhe de Obrigações',
};

type PublicSubview = 'visao-geral' | 'detalhe-caixa' | 'recebiveis' | 'titulos-fundos' | 'a-pagar';

const SUBVIEW_TO_INTERNAL: Record<PublicSubview, SubTab> = {
  'visao-geral': 'overview',
  'detalhe-caixa': 'cash',
  recebiveis: 'receivables',
  'titulos-fundos': 'securitiesFunds',
  'a-pagar': 'payables',
};

const INTERNAL_TO_PUBLIC: Record<SubTab, PublicSubview> = {
  overview: 'visao-geral',
  cash: 'detalhe-caixa',
  receivables: 'recebiveis',
  securitiesFunds: 'titulos-fundos',
  payables: 'a-pagar',
};

const CURRENCY_FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 2,
});
const PERCENT_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const DATE_FORMATTER = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });

const formatOptionalCurrency = (value: number | null) => (value === null ? '-' : CURRENCY_FORMATTER.format(value));

const formatCompactCurrency = (value: number | null) => {
  if (value === null) return '-';
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `R$ ${NUMBER_FORMATTER.format(value / 1_000_000_000)} bi`;
  if (abs >= 1_000_000) return `R$ ${NUMBER_FORMATTER.format(value / 1_000_000)} mi`;
  if (abs >= 1_000) return `R$ ${NUMBER_FORMATTER.format(value / 1_000)} mil`;
  return CURRENCY_FORMATTER.format(value);
};

const formatDateOrDash = (value: string | null) => {
  if (!value) return '-';
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? value : DATE_FORMATTER.format(parsed);
};

const donutBackground = (items: DonutDatum[], colors: string[]) => {
  const total = items.reduce((acc, item) => acc + item.value, 0);
  if (total <= 0 || items.length === 0) return 'hsl(var(--muted))';
  let progress = 0;
  const slices = items.map((item, index) => {
    const start = progress * 100;
    progress += item.value / total;
    const end = progress * 100;
    return `${colors[index % colors.length]} ${start}% ${end}%`;
  });
  return `conic-gradient(${slices.join(', ')})`;
};

function InfoDotTooltip({ text }: { text: string }) {
  return (
    <Tooltip title={text} arrow placement='top'>
      <Box
        component='span'
        sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          border: '1px solid hsl(var(--border))',
          color: 'text.secondary',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 800,
          lineHeight: 1,
          cursor: 'help',
          userSelect: 'none',
        }}
      >
        i
      </Box>
    </Tooltip>
  );
}

function DonutBlock({
  title,
  items,
  colors,
  onViewDetails,
  detailsHref,
}: {
  title: string;
  items: DonutDatum[];
  colors: string[];
  onViewDetails?: () => void;
  detailsHref?: string;
}) {
  const appColors = useAppColors();
  const positiveItems = [...items].filter((item) => item.value > 0).sort((a, b) => b.value - a.value);
  const total = positiveItems.reduce((acc, item) => acc + item.value, 0);
  const totalRaw = items.reduce((acc, item) => acc + item.value, 0);
  if (positiveItems.length === 0 || total <= 0) {
    return (
      <Paper variant='outlined' sx={{ p: 1.25 }}>
        <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 0.6 }}>
          <Typography variant='subtitle2' fontWeight={700}>
            {title}
          </Typography>
          {detailsHref ? (
            <Button
              size='small'
              variant='text'
              component='a'
              href={detailsHref}
              sx={{
                textTransform: 'none',
                minWidth: 0,
                px: 0.5,
                color: appColors.actions.accentOutline.text,
                '&:hover': {
                  color: appColors.actions.accentOutline.hoverText,
                  bgcolor: appColors.actions.accentOutline.hoverBg,
                },
              }}
            >
              Ver detalhes
            </Button>
          ) : onViewDetails ? (
            <Button
              size='small'
              variant='text'
              onClick={onViewDetails}
              sx={{
                textTransform: 'none',
                minWidth: 0,
                px: 0.5,
                color: appColors.actions.accentOutline.text,
                '&:hover': {
                  color: appColors.actions.accentOutline.hoverText,
                  bgcolor: appColors.actions.accentOutline.hoverBg,
                },
              }}
            >
              Ver detalhes
            </Button>
          ) : null}
        </Stack>
        <Typography variant='caption' color='text.secondary'>
          Sem valores positivos no fechamento atual.
        </Typography>
        <Typography variant='body2' fontWeight={700} sx={{ mt: 0.8 }}>
          Total: {formatOptionalCurrency(totalRaw)}
        </Typography>
      </Paper>
    );
  }
  return (
    <Paper variant='outlined' sx={{ p: 1.25 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1 }}>
        <Typography variant='subtitle2' fontWeight={700}>
          {title}
        </Typography>
        {detailsHref ? (
          <Button
            size='small'
            variant='text'
            component='a'
            href={detailsHref}
            sx={{
              textTransform: 'none',
              minWidth: 0,
              px: 0.5,
              color: appColors.actions.accentOutline.text,
              '&:hover': {
                color: appColors.actions.accentOutline.hoverText,
                bgcolor: appColors.actions.accentOutline.hoverBg,
              },
            }}
          >
            Ver detalhes
          </Button>
        ) : onViewDetails ? (
          <Button
            size='small'
            variant='text'
            onClick={onViewDetails}
            sx={{
              textTransform: 'none',
              minWidth: 0,
              px: 0.5,
              color: appColors.actions.accentOutline.text,
              '&:hover': {
                color: appColors.actions.accentOutline.hoverText,
                bgcolor: appColors.actions.accentOutline.hoverBg,
              },
            }}
          >
            Ver detalhes
          </Button>
        ) : null}
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '180px 1fr' },
          gap: 1.2,
          alignItems: 'start',
        }}
      >
        <Stack spacing={1} alignItems='center'>
          <Box
            sx={{
              width: 140,
              height: 140,
              borderRadius: '50%',
              background: donutBackground(positiveItems, colors),
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Box
              sx={{
                width: 84,
                height: 84,
                borderRadius: '50%',
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Typography variant='caption' color='text.secondary'>
                Total
              </Typography>
              <Tooltip title={formatOptionalCurrency(total)} arrow>
                <Typography variant='subtitle2' fontWeight={800} sx={{ cursor: 'help' }}>
                  {formatCompactCurrency(total)}
                </Typography>
              </Tooltip>
            </Box>
          </Box>
        </Stack>
        <Stack spacing={0.8}>
          {positiveItems.map((item, index) => {
            const pct = (item.value / total) * 100;
            return (
              <Stack key={`${title}-${item.label}`} spacing={0.35}>
                <Stack direction='row' justifyContent='space-between' spacing={1}>
                  <Stack direction='row' spacing={0.7} alignItems='center'>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: 999,
                        backgroundColor: colors[index % colors.length],
                      }}
                    />
                    <Typography variant='body2'>{item.label}</Typography>
                  </Stack>
                  <Typography variant='body2' fontWeight={700}>
                    <Tooltip title={formatOptionalCurrency(item.value)} arrow>
                      <Box component='span' sx={{ cursor: 'help' }}>
                        {formatCompactCurrency(item.value)}
                      </Box>
                    </Tooltip>{' '}
                    • {PERCENT_FORMATTER.format(pct)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant='determinate'
                  value={pct}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: 'hsl(var(--muted))',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: colors[index % colors.length],
                    },
                  }}
                />
              </Stack>
            );
          })}
        </Stack>
      </Box>
    </Paper>
  );
}

function CashFlowChart({ points }: { points: CashTabViewModel['history'] }) {
  const appColors = useAppColors();
  const [showCash, setShowCash] = useState(true);
  const [showSecuritiesFunds, setShowSecuritiesFunds] = useState(true);
  const [showRec, setShowRec] = useState(true);
  const [showPay, setShowPay] = useState(true);
  const [showResources, setShowResources] = useState(true);
  const [showResult, setShowResult] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const seriesColors = {
    cash: appColors.charts.summaryHistory.dy,
    securitiesFunds: appColors.charts.summaryHistory.vp,
    receivables: appColors.charts.summaryHistory.price,
    payables: appColors.semantic.dangerStrong,
    resources: appColors.semantic.cyan,
    result: appColors.charts.summaryHistory.benchmark,
  };
  const valid = points.filter(
    (p) =>
      p.cashTotal !== null ||
      p.securitiesAndFundsTotal !== null ||
      p.receivableTotal !== null ||
      p.payableTotal !== null,
  );
  if (valid.length < 2) return null;
  const payableSigned = (value: number | null) => (value === null ? null : -value);
  const resourcesValue = (row: CashTabViewModel['history'][number]) =>
    (row.cashTotal ?? 0) + (row.securitiesAndFundsTotal ?? 0) + (row.receivableTotal ?? 0);
  const resultValue = (row: CashTabViewModel['history'][number]) =>
    resourcesValue(row) + (payableSigned(row.payableTotal) ?? 0);

  const ordered = [...valid].sort((a, b) => a.date.localeCompare(b.date));
  const width = 980;
  const height = 300;
  const left = 80;
  const right = width - 30;
  const top = 22;
  const bottom = height - 48;
  const chartWidth = right - left;
  const chartHeight = bottom - top;

  const activeValues = [
    ...(showCash ? ordered.map((x) => x.cashTotal ?? 0) : [0]),
    ...(showSecuritiesFunds ? ordered.map((x) => x.securitiesAndFundsTotal ?? 0) : [0]),
    ...(showRec ? ordered.map((x) => x.receivableTotal ?? 0) : [0]),
    ...(showPay ? ordered.map((x) => payableSigned(x.payableTotal) ?? 0) : [0]),
    ...(showResources ? ordered.map((x) => resourcesValue(x)) : [0]),
    ...(showResult ? ordered.map((x) => resultValue(x)) : [0]),
  ];
  const domainMinRaw = Math.min(...activeValues);
  const domainMaxRaw = Math.max(...activeValues);
  const domainMin = Math.min(0, domainMinRaw);
  const domainMax = Math.max(1, domainMaxRaw);
  const domainSpan = Math.max(1, domainMax - domainMin);

  const minTs = new Date(`${ordered[0].date}T00:00:00Z`).getTime();
  const maxTs = new Date(`${ordered[ordered.length - 1].date}T00:00:00Z`).getTime();
  const mapX = (date: string) => {
    const ts = new Date(`${date}T00:00:00Z`).getTime();
    if (maxTs === minTs) return left + chartWidth / 2;
    return left + ((ts - minTs) / (maxTs - minTs)) * chartWidth;
  };
  const mapY = (value: number) => bottom - ((value - domainMin) / domainSpan) * chartHeight;

  const poly = (selector: (item: (typeof ordered)[number]) => number | null) =>
    ordered
      .map((item) => {
        const v = selector(item);
        if (v === null) return null;
        return `${mapX(item.date)},${mapY(v)}`;
      })
      .filter((p): p is string => p !== null)
      .join(' ');

  return (
    <Box>
      <Stack direction='row' spacing={0.8} useFlexGap flexWrap='wrap' sx={{ mb: 0.8 }}>
        <Button
          size='small'
          variant={showCash ? 'contained' : 'outlined'}
          onClick={() => setShowCash((v) => !v)}
          sx={{
            textTransform: 'none',
            bgcolor: showCash ? seriesColors.cash : undefined,
            borderColor: seriesColors.cash,
            color: showCash ? appColors.semantic.white : seriesColors.cash,
          }}
        >
          Caixa
        </Button>
        <Button
          size='small'
          variant={showSecuritiesFunds ? 'contained' : 'outlined'}
          onClick={() => setShowSecuritiesFunds((v) => !v)}
          sx={{
            textTransform: 'none',
            bgcolor: showSecuritiesFunds ? seriesColors.securitiesFunds : undefined,
            borderColor: seriesColors.securitiesFunds,
            color: showSecuritiesFunds ? appColors.semantic.white : seriesColors.securitiesFunds,
          }}
        >
          Títulos/fundos
        </Button>
        <Button
          size='small'
          variant={showRec ? 'contained' : 'outlined'}
          onClick={() => setShowRec((v) => !v)}
          sx={{
            textTransform: 'none',
            bgcolor: showRec ? seriesColors.receivables : undefined,
            borderColor: seriesColors.receivables,
            color: showRec ? appColors.semantic.white : seriesColors.receivables,
          }}
        >
          A receber
        </Button>
        <Button
          size='small'
          variant={showPay ? 'contained' : 'outlined'}
          onClick={() => setShowPay((v) => !v)}
          sx={{
            textTransform: 'none',
            bgcolor: showPay ? seriesColors.payables : undefined,
            borderColor: seriesColors.payables,
            color: showPay ? appColors.semantic.white : seriesColors.payables,
          }}
        >
          A pagar
        </Button>
        <Button
          size='small'
          variant={showResources ? 'contained' : 'outlined'}
          onClick={() => setShowResources((v) => !v)}
          sx={{
            textTransform: 'none',
            bgcolor: showResources ? seriesColors.resources : undefined,
            borderColor: seriesColors.resources,
            color: showResources ? appColors.semantic.white : seriesColors.resources,
          }}
        >
          Total recursos
        </Button>
        <Button
          size='small'
          variant={showResult ? 'contained' : 'outlined'}
          onClick={() => setShowResult((v) => !v)}
          sx={{
            textTransform: 'none',
            bgcolor: showResult ? seriesColors.result : undefined,
            borderColor: seriesColors.result,
            color: showResult ? appColors.semantic.white : seriesColors.result,
          }}
        >
          Resultado
        </Button>
      </Stack>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width='100%'
        role='img'
        onMouseLeave={() => setHoveredIndex(null)}
        onMouseMove={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          if (rect.width <= 0 || chartWidth <= 0) return;
          const relativeX = ((event.clientX - rect.left) / rect.width) * width;
          const clampedX = Math.max(left, Math.min(right, relativeX));
          const ratio = (clampedX - left) / chartWidth;
          const index = Math.round(ratio * (ordered.length - 1));
          setHoveredIndex(Math.max(0, Math.min(ordered.length - 1, index)));
        }}
      >
        <rect x={left} y={top} width={chartWidth} height={chartHeight} fill='transparent' stroke='hsl(var(--border))' />
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const y = bottom - t * chartHeight;
          const value = domainMin + domainSpan * t;
          return (
            <g key={t}>
              <line x1={left} y1={y} x2={right} y2={y} stroke='hsl(var(--border))' strokeDasharray='4 4' />
              <text x={left - 8} y={y + 4} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
                {formatCompactCurrency(value)}
              </text>
            </g>
          );
        })}
        {domainMin < 0 ? (
          <line
            x1={left}
            y1={mapY(0)}
            x2={right}
            y2={mapY(0)}
            stroke='hsl(var(--muted-foreground) / 0.8)'
            strokeDasharray='3 3'
          />
        ) : null}
        {showCash ? (
          <polyline
            points={poly((p) => p.cashTotal)}
            fill='none'
            stroke={seriesColors.cash}
            strokeWidth='2'
            strokeDasharray='5 3'
          />
        ) : null}
        {showSecuritiesFunds ? (
          <polyline
            points={poly((p) => p.securitiesAndFundsTotal)}
            fill='none'
            stroke={seriesColors.securitiesFunds}
            strokeWidth='2'
            strokeDasharray='4 3'
          />
        ) : null}
        {showRec ? (
          <polyline
            points={poly((p) => p.receivableTotal)}
            fill='none'
            stroke={seriesColors.receivables}
            strokeWidth='2'
            strokeDasharray='3 3'
          />
        ) : null}
        {showPay ? (
          <polyline
            points={poly((p) => payableSigned(p.payableTotal))}
            fill='none'
            stroke={seriesColors.payables}
            strokeWidth='2'
          />
        ) : null}
        {showResources ? (
          <polyline
            points={poly((p) => resourcesValue(p))}
            fill='none'
            stroke={seriesColors.resources}
            strokeWidth='2'
          />
        ) : null}
        {showResult ? (
          <polyline points={poly((p) => resultValue(p))} fill='none' stroke={seriesColors.result} strokeWidth='2' />
        ) : null}
        {hoveredIndex !== null
          ? (() => {
              const row = ordered[hoveredIndex];
              const x = mapX(row.date);
              return (
                <>
                  <line
                    x1={x}
                    y1={top}
                    x2={x}
                    y2={bottom}
                    stroke='hsl(var(--muted-foreground) / 0.6)'
                    strokeDasharray='4 4'
                  />
                  <g transform={`translate(${Math.min(right - 240, Math.max(left + 8, x + 8))}, ${top + 8})`}>
                    <rect width='240' height='148' rx='8' fill='hsl(var(--card))' stroke='hsl(var(--border))' />
                    <text x='10' y='18' fontSize='12' fill='hsl(var(--muted-foreground))'>
                      {DATE_FORMATTER.format(new Date(`${row.date}T00:00:00Z`))}
                    </text>
                    <text
                      x='10'
                      y='36'
                      fontSize='12'
                      fill={seriesColors.cash}
                    >{`Caixa: ${formatOptionalCurrency(row.cashTotal)}`}</text>
                    <text
                      x='10'
                      y='53'
                      fontSize='12'
                      fill={seriesColors.securitiesFunds}
                    >{`Títulos/fundos: ${formatOptionalCurrency(row.securitiesAndFundsTotal)}`}</text>
                    <text
                      x='10'
                      y='70'
                      fontSize='12'
                      fill={seriesColors.receivables}
                    >{`A receber: ${formatOptionalCurrency(row.receivableTotal)}`}</text>
                    <text
                      x='10'
                      y='87'
                      fontSize='12'
                      fill={seriesColors.payables}
                    >{`Obrigações: ${formatOptionalCurrency(payableSigned(row.payableTotal))}`}</text>
                    <text
                      x='10'
                      y='104'
                      fontSize='12'
                      fill={seriesColors.resources}
                    >{`Total recursos: ${formatOptionalCurrency(resourcesValue(row))}`}</text>
                    <text x='10' y='121' fontSize='12' fill={seriesColors.result}>{`Resultado: ${formatOptionalCurrency(
                      resultValue(row),
                    )}`}</text>
                    <text x='10' y='138' fontSize='11' fill='hsl(var(--muted-foreground))'>
                      {`Cobertura: ${
                        (row.payableTotal ?? 0) > 0
                          ? `${NUMBER_FORMATTER.format(((row.cashTotal ?? 0) + (row.receivableTotal ?? 0)) / (row.payableTotal ?? 1))}x`
                          : '-'
                      }`}
                    </text>
                  </g>
                </>
              );
            })()
          : null}
        <text x={left} y={height - 12} fontSize='12' fill='hsl(var(--muted-foreground))'>
          {DATE_FORMATTER.format(new Date(`${ordered[0].date}T00:00:00Z`))}
        </text>
        <text x={right} y={height - 12} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
          {DATE_FORMATTER.format(new Date(`${ordered[ordered.length - 1].date}T00:00:00Z`))}
        </text>
      </svg>
    </Box>
  );
}

function BreakdownHistoryChart({
  points,
  seriesKeys,
  title,
  colors,
  selector,
}: {
  points: CashTabViewModel['history'];
  seriesKeys: string[];
  title: string;
  colors: string[];
  selector: (row: CashTabViewModel['history'][number]) => BreakdownItem[];
}) {
  const appColors = useAppColors();
  const [visibleKeys, setVisibleKeys] = useState<string[]>(seriesKeys);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const ordered = [...points].sort((a, b) => a.date.localeCompare(b.date));
  const latest = ordered[ordered.length - 1] ?? null;
  const labelsByKey = new Map((latest ? selector(latest) : []).map((item) => [item.key, item.label]));

  useEffect(() => {
    setVisibleKeys((current) => {
      if (!hasUserInteracted) return seriesKeys;
      return current.filter((key) => seriesKeys.includes(key));
    });
  }, [hasUserInteracted, seriesKeys]);

  if (ordered.length < 2 || seriesKeys.length === 0) {
    return (
      <Paper variant='outlined' sx={{ p: 1.25 }}>
        <Typography variant='subtitle2' fontWeight={700}>
          {title}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          Sem histórico suficiente para esse detalhamento.
        </Typography>
      </Paper>
    );
  }

  const series = seriesKeys.map((key) => {
    const values = ordered.map((row) => {
      const found = selector(row).find((item) => item.key === key);
      return found ? found.value : 0;
    });
    return { key, values };
  });
  const colorByKey = new Map(series.map((line, index) => [line.key, colors[index % colors.length]]));

  const visibleSeries = series.filter((item) => visibleKeys.includes(item.key));
  const domainValues = visibleSeries.flatMap((item) => item.values);
  const domainMinRaw = domainValues.length ? Math.min(...domainValues) : 0;
  const domainMaxRaw = domainValues.length ? Math.max(...domainValues) : 1;
  const domainMin = Math.min(0, domainMinRaw);
  const domainMax = Math.max(1, domainMaxRaw);
  const domainSpan = Math.max(1, domainMax - domainMin);

  const width = 980;
  const height = 300;
  const left = 80;
  const right = width - 30;
  const top = 22;
  const bottom = height - 48;
  const chartWidth = right - left;
  const chartHeight = bottom - top;

  const minTs = new Date(`${ordered[0].date}T00:00:00Z`).getTime();
  const maxTs = new Date(`${ordered[ordered.length - 1].date}T00:00:00Z`).getTime();
  const mapX = (date: string) => {
    const ts = new Date(`${date}T00:00:00Z`).getTime();
    if (maxTs === minTs) return left + chartWidth / 2;
    return left + ((ts - minTs) / (maxTs - minTs)) * chartWidth;
  };
  const mapY = (value: number) => bottom - ((value - domainMin) / domainSpan) * chartHeight;

  const poly = (values: number[]) => values.map((value, idx) => `${mapX(ordered[idx].date)},${mapY(value)}`).join(' ');

  return (
    <Paper variant='outlined' sx={{ p: 1.25 }}>
      <Typography variant='subtitle2' fontWeight={700} sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Stack direction='row' spacing={0.8} useFlexGap flexWrap='wrap' sx={{ mb: 0.8 }}>
        {series.map((line, index) => {
          const isVisible = visibleKeys.includes(line.key);
          const label = labelsByKey.get(line.key) ?? line.key;
          return (
            <Button
              key={line.key}
              size='small'
              variant={isVisible ? 'contained' : 'outlined'}
              onClick={() => {
                setHasUserInteracted(true);
                setVisibleKeys((current) =>
                  current.includes(line.key) ? current.filter((item) => item !== line.key) : [...current, line.key],
                );
              }}
              sx={{
                textTransform: 'none',
                bgcolor: isVisible ? colors[index % colors.length] : undefined,
                borderColor: colors[index % colors.length],
                color: isVisible ? appColors.semantic.white : colors[index % colors.length],
              }}
            >
              {label}
            </Button>
          );
        })}
      </Stack>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width='100%'
        role='img'
        onMouseLeave={() => setHoveredIndex(null)}
        onMouseMove={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          if (rect.width <= 0 || chartWidth <= 0) return;
          const relativeX = ((event.clientX - rect.left) / rect.width) * width;
          const clampedX = Math.max(left, Math.min(right, relativeX));
          const ratio = (clampedX - left) / chartWidth;
          const index = Math.round(ratio * (ordered.length - 1));
          setHoveredIndex(Math.max(0, Math.min(ordered.length - 1, index)));
        }}
      >
        <rect x={left} y={top} width={chartWidth} height={chartHeight} fill='transparent' stroke='hsl(var(--border))' />
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const y = bottom - t * chartHeight;
          const value = domainMin + domainSpan * t;
          return (
            <g key={t}>
              <line x1={left} y1={y} x2={right} y2={y} stroke='hsl(var(--border))' strokeDasharray='4 4' />
              <text x={left - 8} y={y + 4} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
                {formatCompactCurrency(value)}
              </text>
            </g>
          );
        })}
        {domainMin < 0 ? (
          <line
            x1={left}
            y1={mapY(0)}
            x2={right}
            y2={mapY(0)}
            stroke='hsl(var(--muted-foreground) / 0.8)'
            strokeDasharray='3 3'
          />
        ) : null}
        {visibleSeries.map((line) => (
          <polyline
            key={line.key}
            points={poly(line.values)}
            fill='none'
            stroke={colorByKey.get(line.key) ?? colors[0]}
            strokeWidth='2'
          />
        ))}
        {hoveredIndex !== null
          ? (() => {
              const row = ordered[hoveredIndex];
              const x = mapX(row.date);
              const currentBreakdown = selector(row);
              return (
                <>
                  <line
                    x1={x}
                    y1={top}
                    x2={x}
                    y2={bottom}
                    stroke='hsl(var(--muted-foreground) / 0.6)'
                    strokeDasharray='4 4'
                  />
                  <g transform={`translate(${Math.min(right - 280, Math.max(left + 8, x + 8))}, ${top + 8})`}>
                    <rect width='280' height='150' rx='8' fill='hsl(var(--card))' stroke='hsl(var(--border))' />
                    <text x='10' y='18' fontSize='12' fill='hsl(var(--muted-foreground))'>
                      {DATE_FORMATTER.format(new Date(`${row.date}T00:00:00Z`))}
                    </text>
                    {visibleSeries.slice(0, 6).map((line, index) => {
                      const found = currentBreakdown.find((item) => item.key === line.key);
                      const label = labelsByKey.get(line.key) ?? line.key;
                      return (
                        <text
                          key={line.key}
                          x='10'
                          y={36 + index * 18}
                          fontSize='12'
                          fill={colorByKey.get(line.key) ?? colors[0]}
                        >{`${label}: ${formatOptionalCurrency(found?.value ?? 0)}`}</text>
                      );
                    })}
                  </g>
                </>
              );
            })()
          : null}
        <text x={left} y={height - 12} fontSize='12' fill='hsl(var(--muted-foreground))'>
          {DATE_FORMATTER.format(new Date(`${ordered[0].date}T00:00:00Z`))}
        </text>
        <text x={right} y={height - 12} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
          {DATE_FORMATTER.format(new Date(`${ordered[ordered.length - 1].date}T00:00:00Z`))}
        </text>
      </svg>
    </Paper>
  );
}

export function TickerCashTabClient({ tickerLabel, data, activeSubview, buildHref }: Props) {
  const appColors = useAppColors();
  const initialSubview: SubTab = activeSubview ? SUBVIEW_TO_INTERNAL[activeSubview] : 'overview';
  const [active, setActive] = useState<SubTab>(initialSubview);

  useEffect(() => {
    if (!activeSubview) return;
    setActive(SUBVIEW_TO_INTERNAL[activeSubview]);
  }, [activeSubview]);

  const handleSelect = (section: SubTab) => {
    setActive(section);
    if (buildHref) {
      // Navigation handled by the parent (anchor with href) so just keep
      // the local state in sync for the visual transition.
      void buildHref;
    }
  };
  const cashColors = [
    appColors.charts.summaryHistory.vp,
    appColors.charts.summaryHistory.dy,
    appColors.charts.summaryHistory.price,
    appColors.charts.summaryHistory.benchmark,
  ];
  const securitiesFundsColors = [
    appColors.charts.market.ifix,
    appColors.charts.market.ibov,
    appColors.charts.market.idiv,
    appColors.charts.market.cdi,
    appColors.charts.market.pib,
  ];
  const receivableColors = [
    appColors.charts.summaryHistory.price,
    appColors.charts.summaryHistory.dy,
    appColors.charts.market.ifix,
    appColors.charts.market.ibov,
    appColors.charts.market.idiv,
  ];
  const payableColors = [
    appColors.semantic.dangerStrong,
    appColors.charts.market.dbgg,
    appColors.charts.market.cdi,
    appColors.charts.market.pib,
    appColors.charts.market.dlsp,
  ];

  const cashItems = useMemo(
    () =>
      data.current.cashBreakdown.map((item) => ({
        label: item.label,
        value: item.value,
      })),
    [data.current.cashBreakdown],
  );

  const receivableItems = useMemo(
    () =>
      data.current.receivablesBreakdown.map((item) => ({
        label: item.label,
        value: item.value,
      })),
    [data.current.receivablesBreakdown],
  );

  const payableItems = useMemo(
    () =>
      data.current.payablesBreakdown.map((item) => ({
        label: item.label,
        value: item.value,
      })),
    [data.current.payablesBreakdown],
  );
  const securitiesFundsItems = useMemo(
    () =>
      data.current.securitiesAndFundsBreakdown.map((item) => ({
        label: item.label,
        value: item.value,
      })),
    [data.current.securitiesAndFundsBreakdown],
  );

  const resolveNonZeroSeriesKeys = (current: BreakdownItem[], history: Array<BreakdownItem[]>) => {
    const totalsByKey = new Map<string, number>();
    const allRows = [current, ...history];
    for (const row of allRows) {
      for (const item of row) {
        totalsByKey.set(item.key, (totalsByKey.get(item.key) ?? 0) + Math.abs(item.value));
      }
    }
    return Array.from(totalsByKey.entries())
      .filter(([, totalAbs]) => totalAbs > 0)
      .map(([key]) => key);
  };

  const cashSeriesKeys = useMemo(() => {
    return resolveNonZeroSeriesKeys(
      data.current.cashBreakdown,
      data.history.map((row) => row.cashBreakdown),
    );
  }, [data.current.cashBreakdown, data.history]);

  const receivableSeriesKeys = useMemo(() => {
    return resolveNonZeroSeriesKeys(
      data.current.receivablesBreakdown,
      data.history.map((row) => row.receivablesBreakdown),
    );
  }, [data.current.receivablesBreakdown, data.history]);

  const payableSeriesKeys = useMemo(() => {
    return resolveNonZeroSeriesKeys(
      data.current.payablesBreakdown,
      data.history.map((row) => row.payablesBreakdown),
    );
  }, [data.current.payablesBreakdown, data.history]);
  const securitiesFundsSeriesKeys = useMemo(() => {
    return resolveNonZeroSeriesKeys(
      data.current.securitiesAndFundsBreakdown,
      data.history.map((row) => row.securitiesAndFundsBreakdown),
    );
  }, [data.current.securitiesAndFundsBreakdown, data.history]);

  const pressureMonths = data.history.filter((row) => {
    const resources = (row.cashTotal ?? 0) + (row.receivableTotal ?? 0);
    return (row.payableTotal ?? 0) > resources;
  }).length;

  const content = (() => {
    if (active === 'cash') {
      return (
        <Stack spacing={1.5}>
          <DonutBlock title='Caixa por natureza (valor atual)' items={cashItems} colors={cashColors} />
          <BreakdownHistoryChart
            points={data.history}
            seriesKeys={cashSeriesKeys}
            title='Histórico de caixa (itens do breakdown)'
            colors={cashColors}
            selector={(row) => row.cashBreakdown}
          />
        </Stack>
      );
    }

    if (active === 'receivables') {
      return (
        <Stack spacing={1.5}>
          <DonutBlock title='Recebíveis por origem (valor atual)' items={receivableItems} colors={receivableColors} />
          <BreakdownHistoryChart
            points={data.history}
            seriesKeys={receivableSeriesKeys}
            title='Histórico de recebíveis (itens do breakdown)'
            colors={receivableColors}
            selector={(row) => row.receivablesBreakdown}
          />
        </Stack>
      );
    }

    if (active === 'payables') {
      return (
        <Stack spacing={1.5}>
          <DonutBlock title='Obrigações por natureza (valor atual)' items={payableItems} colors={payableColors} />
          <BreakdownHistoryChart
            points={data.history}
            seriesKeys={payableSeriesKeys}
            title='Histórico de obrigações (itens do breakdown)'
            colors={payableColors}
            selector={(row) => row.payablesBreakdown}
          />
        </Stack>
      );
    }

    if (active === 'securitiesFunds') {
      return (
        <Stack spacing={1.5}>
          <DonutBlock
            title='Títulos e fundos (valor atual)'
            items={securitiesFundsItems}
            colors={securitiesFundsColors}
          />
          <BreakdownHistoryChart
            points={data.history}
            seriesKeys={securitiesFundsSeriesKeys}
            title='Histórico de títulos e fundos (itens do breakdown)'
            colors={securitiesFundsColors}
            selector={(row) => row.securitiesAndFundsBreakdown}
          />
        </Stack>
      );
    }

    return (
      <Stack spacing={1.5}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.2,
          }}
        >
          <DonutBlock
            title='Caixa (valor atual)'
            items={cashItems}
            colors={cashColors}
            onViewDetails={buildHref ? undefined : () => handleSelect('cash')}
            detailsHref={buildHref?.('detalhe-caixa')}
          />
          <DonutBlock
            title='Títulos e fundos (valor atual)'
            items={securitiesFundsItems}
            colors={securitiesFundsColors}
            onViewDetails={buildHref ? undefined : () => handleSelect('securitiesFunds')}
            detailsHref={buildHref?.('titulos-fundos')}
          />
          <DonutBlock
            title='A receber (valor atual)'
            items={receivableItems}
            colors={receivableColors}
            onViewDetails={buildHref ? undefined : () => handleSelect('receivables')}
            detailsHref={buildHref?.('recebiveis')}
          />
          <DonutBlock
            title='Obrigações (valor atual)'
            items={payableItems}
            colors={payableColors}
            onViewDetails={buildHref ? undefined : () => handleSelect('payables')}
            detailsHref={buildHref?.('a-pagar')}
          />
        </Box>
        <Paper variant='outlined' sx={{ p: 1.25 }}>
          <Stack direction='row' spacing={0.6} alignItems='center' sx={{ mb: 1 }}>
            <Typography variant='subtitle2' fontWeight={700}>
              Fluxo e cobertura
            </Typography>
            <InfoDotTooltip text='Caixa líquido = caixa + liquidez financeira + a receber - a pagar.' />
          </Stack>
          <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap' sx={{ mb: 1 }}>
            <Chip
              size='small'
              variant='outlined'
              label={`Cobertura atual: ${data.current.coverage === null ? '-' : `${NUMBER_FORMATTER.format(data.current.coverage)}x`}`}
            />
            <Chip size='small' variant='outlined' label={`Meses sob pressão: ${pressureMonths}`} />
            <Chip
              size='small'
              variant='outlined'
              label={`Caixa líquido atual: ${formatCompactCurrency(data.current.netCashCurrent)}`}
            />
            <Chip
              size='small'
              variant='outlined'
              label={`Títulos/fundos m/m: ${data.current.monthlyDeltaSecuritiesAndFunds === null ? '-' : formatCompactCurrency(data.current.monthlyDeltaSecuritiesAndFunds)}`}
            />
          </Stack>
          <CashFlowChart points={data.history} />
        </Paper>
      </Stack>
    );
  })();

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }}>
        <Paper variant='outlined' sx={SIDEBAR_TAB_PAPER_SX}>
          <Typography variant='overline' sx={SIDEBAR_TAB_HEADER_SX}>
            Caixa
          </Typography>
          <Stack direction='column' spacing={0.5}>
            {(Object.keys(SECTION_LABEL) as SubTab[]).map((section) => {
              const publicSubview = INTERNAL_TO_PUBLIC[section];
              const href = buildHref?.(publicSubview) ?? `#${section}`;
              return (
                <Button
                  key={section}
                  component='a'
                  href={href}
                  onClick={() => handleSelect(section)}
                  variant='text'
                  sx={sidebarTabButtonSx(active === section)}
                >
                  {SECTION_LABEL[section]}
                </Button>
              );
            })}
          </Stack>
        </Paper>
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {active === 'overview' ? (
            <Stack spacing={2}>
              <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
                <Typography variant='h6' fontWeight={800}>
                  Caixa de {tickerLabel}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Fonte: {data.sourceLabel} | Referência: {formatDateOrDash(data.referenceDate)} | CNPJ:{' '}
                  {data.cnpjClasse ?? '-'}
                </Typography>
                <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap' sx={{ mt: 1.2 }}>
                  <Chip
                    size='small'
                    variant='outlined'
                    label={`Cobertura: ${data.current.coverage === null ? '-' : `${NUMBER_FORMATTER.format(data.current.coverage)}x`}`}
                  />
                  <Chip
                    size='small'
                    variant='outlined'
                    label={`Caixa líquido: ${formatCompactCurrency(data.current.netCashCurrent)}`}
                  />
                  <Chip
                    size='small'
                    variant='outlined'
                    label={`Recebíveis m/m: ${data.current.monthlyDeltaReceivable === null ? '-' : formatCompactCurrency(data.current.monthlyDeltaReceivable)}`}
                  />
                  <Chip
                    size='small'
                    variant='outlined'
                    label={`Obrigações m/m: ${data.current.monthlyDeltaPayable === null ? '-' : formatCompactCurrency(data.current.monthlyDeltaPayable)}`}
                  />
                </Stack>
              </Paper>
              {content}
            </Stack>
          ) : (
            content
          )}
        </Box>
      </Stack>
    </Stack>
  );
}
